import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import { putStudentPassword } from '../../../api/utils/student/profile/apis';
import FormRow from '../../common/input/FormRow';
import { Field, ReadOnlyField } from '../../common/form/Field';
import SectionHeader from '../../common/form/SectionHeader';
import AccordionPanel, {
  AccordionControlProps,
} from '../../common/form/AccordionPanel';
import { passwordRules } from '../../../constants/validation';
import { openAlert } from '../../../store/features/modalSlice';
import { useAppDispatch } from '../../../store/hook';
import { PutStudentPasswordRequest } from '../../../constants/types/student/profile/type';

const EMPTY_FORM: PutStudentPasswordRequest = {
  oldPassword: '',
  newPassword: '',
  newPassword2: '',
};

/**
 * 학생 페이지 > 내 정보 > 비밀번호 변경 영역
 * 저장은 이 영역이 직접 처리한다.
 */
const PasswordSection = ({ section }: { section: AccordionControlProps }) => {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<PutStudentPasswordRequest>({ defaultValues: EMPTY_FORM });
  const newPassword = watch('newPassword');

  const isEditing = section.isEditing;

  /** 입력값을 비우고 수정 상태를 끝낸다. (저장 성공/취소 공통) */
  const close = () => {
    reset(EMPTY_FORM);
    section.onClose();
  };

  const onSubmit = async (data: PutStudentPasswordRequest) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await putStudentPassword(data);
      dispatch(openAlert({ message: '비밀번호가 변경되었습니다.' }));
      close();
    } catch (e) {
      console.error(e);
      // 네트워크/500 에러는 인터셉터가 이미 알럿을 띄운다. (__handled)
      if ((e as { __handled?: boolean })?.__handled) return;
      const message = isAxiosError(e)
        ? (e.response?.data?.error ?? e.response?.data?.message)
        : undefined;
      dispatch(
        openAlert({ message: message ?? '비밀번호 변경에 실패했습니다.' }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section>
      <SectionHeader
        title="비밀번호 변경"
        isEditing={isEditing}
        disabled={section.disabled}
        isSaving={isSaving}
        onEdit={section.onEdit}
        onCancel={close}
        onSave={handleSubmit(onSubmit)}
        isOpen={section.isOpen}
        onToggle={section.onToggle}
        editLabel="변경하기"
      />

      <AccordionPanel isOpen={section.isOpen}>
        {isEditing ? (
          <div className="mt-5 grid grid-cols-1 gap-4 pb-2 xl:grid-cols-2">
            <Field label="현재 비밀번호" error={errors.oldPassword?.message}>
              <FormRow
                type="password"
                name="oldPassword"
                control={control}
                placeholder="현재 비밀번호 입력"
                rules={{ required: '현재 비밀번호를 입력해주세요' }}
                error={!!errors.oldPassword}
                autoComplete="off"
              />
            </Field>

            {/* 현재 비밀번호 옆 빈 영역 */}
            <div className="hidden xl:block" />

            <Field label="새 비밀번호" error={errors.newPassword?.message}>
              <FormRow
                type="password"
                name="newPassword"
                control={control}
                placeholder="새 비밀번호 입력"
                rules={passwordRules({
                  required: '새 비밀번호를 입력해주세요',
                })}
                error={!!errors.newPassword}
                autoComplete="off"
              />
            </Field>

            <Field
              label="새 비밀번호 확인"
              error={errors.newPassword2?.message}
            >
              <FormRow
                type="password"
                name="newPassword2"
                control={control}
                placeholder="새 비밀번호 다시 입력"
                rules={{
                  required: '새 비밀번호 확인을 입력해주세요',
                  validate: (value) =>
                    value === newPassword || '새 비밀번호와 일치하지 않습니다',
                }}
                error={!!errors.newPassword2}
                autoComplete="off"
              />
            </Field>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 pb-2 xl:grid-cols-2">
            {/* 비밀번호는 보여줄 수 없으므로 자리만 표시한다. */}
            <ReadOnlyField label="비밀번호" value="••••••••" />
          </div>
        )}
      </AccordionPanel>
    </section>
  );
};

export default PasswordSection;
